import { isNil } from 'lodash';
import moment from 'moment';
import { CSSProperties, useCallback } from 'react';
import { formatMillisecondsToDurationString } from '@shared/components/timer/utils';
import {
  TimeTrackerComponentData,
  TimeTrackerField,
} from '@shared/data/entity/fields/TimeTrackerField';
import { WorkflowTimeTrackerComponent } from '@shared/scenes/workflows/views/view-components/WorkflowTimeTrackerComponent';
import { API } from '@shared/types/api';
import { useResponsiveFormContext } from '../ResponsiveFormContext';
import { EditFieldContainer } from './EditFieldContainer';

interface TimeTrackerEditFieldProps {
  component: API.TimeTrackerComponent;
  field: TimeTrackerField | null;
  updateField: (componentId: string, field: TimeTrackerField) => void;
  error?: string;
  style?: CSSProperties;
}

export function TimeTrackerEditField({
  component,
  field,
  updateField,
  style,
  error,
}: TimeTrackerEditFieldProps) {
  const responsiveForms = useResponsiveFormContext();
  const handleDataChanged = useCallback(
    (id: string, entries: TimeTrackerComponentData) => {
      updateField(id, new TimeTrackerField(entries));
    },
    [updateField],
  );

  const printContent =
    field?.timerEntries
      ?.map(entry => {
        const start = new Date(entry.start.timestamp);
        const end = entry.end ? new Date(entry.end.timestamp) : null;
        const duration = !isNil(end)
          ? formatMillisecondsToDurationString(end.getTime() - start.getTime(), false)
          : 'Running';
        const startStr = moment(start).format('MMM D, h:mm a');
        const endStr = !isNil(end) ? moment(end).format('MMM D, h:mm a') : '...';
        return `${startStr} \u2013 ${endStr} (${duration})`;
      })
      .join('\n') || undefined;

  if (responsiveForms) {
    return (
      <EditFieldContainer
        component={component}
        style={style}
        printContent={printContent}
        error={error}
      >
        <WorkflowTimeTrackerComponent
          component={component}
          initialData={field?.timerEntries}
          onDataChanged={handleDataChanged}
          isReadOnly={component.readonly === true}
          paddingHorizontal={0}
          iconPad={2}
        />
      </EditFieldContainer>
    );
  } else {
    return (
      <WorkflowTimeTrackerComponent
        component={component}
        initialData={field?.timerEntries}
        onDataChanged={handleDataChanged}
        isReadOnly={component.readonly === true}
        paddingHorizontal={3}
        iconPad={2}
      />
    );
  }
}
